import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { requestPasswordReset } from '../../services/AuthService';

/**
 * Página para solicitar redefinição de senha
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Email inválido';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validar formulário antes de enviar
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Chama o serviço para solicitar redefinição de senha
      await requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      console.error('Erro ao solicitar redefinição de senha:', err);

      if (err.response) {
        if (err.response.data && err.response.data.detail) {
          setError(err.response.data.detail);
        } else {
          setError(`Erro ${err.response.status}: Falha ao solicitar redefinição de senha`);
        }
      } else if (err.request) {
        setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      } else {
        setError('Erro ao processar solicitação: ' + (err.message || 'Erro desconhecido'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, minWidth: 320, maxWidth: 500 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Esqueci minha senha
        </Typography>

        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Se o email informado estiver registrado em nossa plataforma, você receberá um link
              para redefinir sua senha.
            </Alert>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Verifique sua caixa de entrada e também a pasta de spam.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate('/login')}
            >
              Voltar para Login
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Informe seu email cadastrado para receber um link de redefinição de senha.
            </Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              sx={{ mb: 2 }}
              autoFocus
              autoComplete="email"
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Enviar link de redefinição'}
            </Button>
            <Button component={Link} to="/login" variant="text" fullWidth>
              Voltar para Login
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}
